export type FmbDishDietaryTypeCsv = "VEGETARIAN" | "NON_VEGETARIAN";

export type ParsedFmbDishRow = {
  name: string;
  dietaryType: FmbDishDietaryTypeCsv;
  description: string | null;
  imageUrl: string | null;
  /** 1-based row number in the original CSV (header is row 1). */
  sourceRowNumber: number;
};

export type FmbDishCsvParseError = {
  rowNumber: number;
  message: string;
};

export type ParseFmbDishCsvResult = {
  rows: ParsedFmbDishRow[];
  parseErrors: FmbDishCsvParseError[];
  skippedBlankRows: number;
};

const NAME_HEADER_KEYS = ["name", "dish", "dishname", "menuname", "menu"];
const DIETARY_HEADER_KEYS = ["dietarytype", "dietary", "diet", "menutype", "type"];
const DESCRIPTION_HEADER_KEYS = ["description", "desc", "notes", "details"];
const IMAGE_HEADER_KEYS = ["imageurl", "image", "picture", "photo", "url"];

function normalizeHeader(cell: string): string {
  return cell.trim().replace(/^"|"$/g, "").trim();
}

function normalizeHeaderKey(cell: string): string {
  return normalizeHeader(cell)
    .toLowerCase()
    .replace(/[\s-_]/g, "");
}

function parseCsvLine(line: string): string[] {
  const out: string[] = [];
  let cur = "";
  let inQ = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (c === '"') {
      inQ = !inQ;
      continue;
    }
    if (!inQ && c === ",") {
      out.push(cur);
      cur = "";
      continue;
    }
    cur += c;
  }
  out.push(cur);
  return out;
}

function parseCsvGrid(content: string): string[][] {
  const text = content.replace(/^\uFEFF/, "");
  const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
  return lines.map(parseCsvLine);
}

function findColumnIndex(headers: string[], candidates: string[]): number {
  const normalized = headers.map((h) => normalizeHeaderKey(h));
  for (const key of candidates) {
    const idx = normalized.indexOf(key);
    if (idx >= 0) {
      return idx;
    }
  }
  return -1;
}

/** Map flexible dietary labels to API enum values. */
export function mapDietaryTypeCell(raw: string): FmbDishDietaryTypeCsv | null {
  const t = normalizeHeader(raw)
    .toLowerCase()
    .replace(/[\s-_]/g, "");
  if (!t) return null;
  if (t === "vegetarian" || t === "veg") return "VEGETARIAN";
  if (t === "nonvegetarian" || t === "nonveg" || t === "nonvegitarian" || t === "nv") {
    return "NON_VEGETARIAN";
  }
  return null;
}

/**
 * Parse dish CSV in the browser. Requires name + dietary type columns.
 * Duplicate names (case-insensitive) keep the last row.
 */
export function parseFmbDishCsv(content: string): ParseFmbDishCsvResult {
  const grid = parseCsvGrid(content);
  if (grid.length < 2) {
    throw new Error("CSV must include a header row and at least one data row");
  }

  const headerRow = grid[0]!;
  const nameCol = findColumnIndex(headerRow, NAME_HEADER_KEYS);
  if (nameCol < 0) {
    throw new Error(
      `No name column found. Use one of these headers: ${NAME_HEADER_KEYS.join(", ")}`
    );
  }
  const dietaryCol = findColumnIndex(headerRow, DIETARY_HEADER_KEYS);
  if (dietaryCol < 0) {
    throw new Error(
      `No dietary type column found. Use one of these headers: ${DIETARY_HEADER_KEYS.join(", ")}`
    );
  }

  const descriptionCol = findColumnIndex(headerRow, DESCRIPTION_HEADER_KEYS);
  const imageCol = findColumnIndex(headerRow, IMAGE_HEADER_KEYS);

  const byName = new Map<string, ParsedFmbDishRow>();
  const parseErrors: FmbDishCsvParseError[] = [];
  let skippedBlankRows = 0;

  for (let i = 1; i < grid.length; i++) {
    const cells = grid[i]!;
    const sourceRowNumber = i + 1;
    const name = normalizeHeader(cells[nameCol] ?? "").trim();
    const dietaryRaw = normalizeHeader(cells[dietaryCol] ?? "").trim();
    const description =
      descriptionCol >= 0 ? normalizeHeader(cells[descriptionCol] ?? "").trim() || null : null;
    const imageUrl = imageCol >= 0 ? normalizeHeader(cells[imageCol] ?? "").trim() || null : null;

    const hasAny = name || dietaryRaw || (description ?? "") || (imageUrl ?? "");

    if (!hasAny) {
      skippedBlankRows += 1;
      continue;
    }

    if (!name) {
      parseErrors.push({ rowNumber: sourceRowNumber, message: "Missing name" });
      continue;
    }
    if (name.length > 255) {
      parseErrors.push({
        rowNumber: sourceRowNumber,
        message: "Dish name exceeds 255 characters",
      });
      continue;
    }

    const dietaryType = mapDietaryTypeCell(dietaryRaw);
    if (!dietaryType) {
      parseErrors.push({
        rowNumber: sourceRowNumber,
        message: dietaryRaw
          ? `Invalid dietary type "${dietaryRaw}" (use VEGETARIAN or NON_VEGETARIAN)`
          : "Missing dietary type",
      });
      continue;
    }

    if (imageUrl && imageUrl.length > 2000) {
      parseErrors.push({
        rowNumber: sourceRowNumber,
        message: "Image URL exceeds 2000 characters",
      });
      continue;
    }

    byName.set(name.toLowerCase(), {
      name,
      dietaryType,
      description,
      imageUrl,
      sourceRowNumber,
    });
  }

  if (byName.size === 0 && parseErrors.length === 0) {
    throw new Error("No dish rows found in CSV");
  }

  return {
    rows: [...byName.values()],
    parseErrors,
    skippedBlankRows,
  };
}

/** CSV template content for bulk dish import. */
export const FMB_DISH_CSV_TEMPLATE = [
  "name,dietaryType,description,imageUrl",
  "Dal Tadka,VEGETARIAN,Yellow lentils with tadka,",
  "Chicken Biryani,NON_VEGETARIAN,,",
].join("\n");

export function downloadFmbDishCsvTemplate(): void {
  const blob = new Blob([`${FMB_DISH_CSV_TEMPLATE}\n`], {
    type: "text/csv;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "fmb-dishes-import-template.csv";
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
